import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../Config/Config';
import { AtSign, Lock, Eye, EyeOff } from 'lucide-react';
import logo from '../../assets/finance7.png';
import LoaderSpinner from '../Essentials/LoaderSpinner';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoading(true);

    signInWithEmailAndPassword(auth, email, password)
      .then(() => {
        setSuccessMsg('Logged in successfully, redirecting...');
        setEmail('');
        setPassword('');
        setErrorMsg('');
        setTimeout(() => {
          setSuccessMsg('');
          navigate('/dashboard');
        }, 3000);
      })
      .catch((error) => {
        if (error.message.includes('network-request-failed')) {
          setErrorMsg('Please check your Internet connection.');
        } else if (error.message.includes('invalid-credential')) {
          setErrorMsg('Incorrect email or password.');
        } else {
          setErrorMsg(error.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  return (
    <div 
      className="min-h-screen bg-[#000300] py-16 px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center relative z-[1100]"
      style={{ paddingTop: 'calc(2rem + 2px)' }}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-32 h-32 bg-[#00df9a] opacity-10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-[#00df9a] opacity-10 rounded-full filter blur-3xl"></div>
      </div>
      
      <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden z-10">
        {/* Premium Header with gradient */}
        <div className="bg-[#000300] py-6 px-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#00df9a] rounded-full filter blur-xl"></div>
          </div>
          <div className="relative z-10">
            <img src={logo} alt="Company Logo" className="h-8 sm:h-9 md:h-10 lg:h-10 mx-auto mb-3" />
            <p className="text-xs sm:text-sm md:text-md lg:text-md text-gray-300 mt-1">Sign in to access your account</p>
          </div>
        </div>

        <div className="p-8 md:p-10">
          {successMsg && (
            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-100 shadow-sm">
              <p className="text-green-700 font-medium">{successMsg}</p>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleLogin}>
            {/* Email */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 tracking-wide">Email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <AtSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  className="block w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#000300] focus:border-transparent transition-all duration-200"
                  placeholder="john@example.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 tracking-wide">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type={passwordVisible ? "text" : "password"}
                  className="block w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#000300] focus:border-transparent transition-all duration-200"
                  placeholder="••••••••"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {passwordVisible ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                  )}
                </button>
              </div>
            </div>

            {/* Forget Password Link */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-[#000300] focus:ring-[#000300] border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>
              <Link 
                to="/resetpassword" 
                className="text-sm text-[#000300] hover:text-gray-800 font-medium underline"
              >
                Forgot password?
              </Link>
            </div>

            {/* Sign In Button */}
            <div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#000300] hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#000300] transition-all duration-200 ${
                  loading ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {loading ? <LoaderSpinner /> : 'Sign In'}
              </button>
            </div>
          </form>

          {errorMsg && (
            <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-100 shadow-sm">
              <p className="text-red-700 font-medium">{errorMsg}</p>
            </div>
          )}

          <div className="mt-6 text-center text-sm">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link to="/signup" className="text-[#000300] hover:text-gray-800 font-medium underline">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;